import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { endpoints, getIDFromUrl } from '@rossum/api-client';
import { SectionData } from '@rossum/api-client/annotations';
import { EvaluateRulesResponse } from '@rossum/api-client/internal';
import { QueueRule } from '@rossum/api-client/schemas';
import { api } from '../../../../lib/apiClient';
import { RULE_PREVIEW_KEY } from '../../keys';
import {
  ContentForPreview,
  makeArtificialContent,
} from './makeArtificialContent';

type ResponseExtension = {
  annotationUrl: string;
  annotationContent: Array<SectionData> | undefined;
};

export type UseCalculateRulesPreviewResult = UseQueryResult<
  EvaluateRulesResponse & ResponseExtension
>;

export const useCalculateRulesPreview = (
  content: ContentForPreview[],
  disabled?: boolean,
  schemaRules: Partial<QueueRule>[] = []
) => {
  const intl = useIntl();
  const queries = content.map(item => {
    const queryKey = [
      RULE_PREVIEW_KEY,
      'annotation-content',
      item,
      schemaRules,
    ] as const;

    return {
      queryKey,
      queryFn: async () => {
        const annotationContent =
          item.type === 'from_annotation'
            ? await api
                .request(
                  endpoints.annotations.getContent(
                    getIDFromUrl(item.annotationUrl)
                  )
                )
                .then(({ results }) => results)
            : makeArtificialContent(item.dependencies, item.schemaContent);

        return (
          api
            .request(
              endpoints.internal.rules.evaluateRules({
                annotationContent,
                schemaRules,
                queue:
                  item.type === 'from_annotation'
                    ? item.annotationQueue
                    : item.queue,
                annotation:
                  item.type === 'from_annotation'
                    ? item.annotationUrl
                    : undefined,
              })
            )
            // When receiving an error, it will still be considered as a success in react query
            // This will stream-line the error handling, as it will be handled the same way the errors in conditions are
            .catch(
              (): EvaluateRulesResponse => ({
                conditionValues: [],
                messages: [
                  {
                    content: intl.formatMessage({
                      id: 'features.formulas.preview.failed',
                    }),
                    type: 'error',
                  },
                ],
                actions: [],
              })
            )
            .then(response => ({
              ...response,
              content: item,
              // content should not change during evaluation
              annotationContent,
            }))
        );
      },
      enabled: schemaRules.length > 0 && !disabled,
    };
  });

  return useQueries({ queries });
};
